import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  Collapse,
  FormControlLabel,
  Divider,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight, Search } from "@mui/icons-material";
import { formatCurrency, formatPercentage } from "@/utils/format";
import { useDebounce } from "@/hooks/useDebounce";
import {
  chaveDoFiltro,
  DICA_ESTIMATIVA_PERDA,
  DIMENSAO_LABELS,
  DIMENSOES,
  ehSemValor,
  filtroDaDimensao,
  getRupturaProdutos,
  getRupturaProdutosTotal,
  PROBLEMAS,
  PRODUTOS_POR_PAGINA,
  resolveEmpresaRef,
  SITUACOES,
  type RupturaDimensao,
  type RupturaFiltros,
  type RupturaProduto,
} from "@/api/ruptura.service";
import { LinkProduto } from "@/components/panel/LinkProduto";
import { GradeInfo } from "./RupturaCards";
import { formatNumero, percDe, ProblemaChip, RodapeCard, SEM_FILTRO, Voltar, type NomeLoja } from "./rupturaComum";
import { LojasRupturaContext, useRupturaBusca, useRupturaResumo } from "./useRupturaBusca";

// Lista de produtos da ruptura, com busca e filtros.

function ProdutoCard({
  produto,
  nomeLoja,
  onVerDetalhe,
}: {
  produto: RupturaProduto;
  nomeLoja: NomeLoja;
  onVerDetalhe: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const solucao = produto.solucao
    ? resolveEmpresaRef(produto.solucao, produto.cod_empresa_ref ? nomeLoja(produto.cod_empresa_ref) : null)
    : "";

  return (
    <Card variant="outlined">
      {/* div, não button: o card leva o link do produto dentro. */}
      <CardActionArea component="div" onClick={() => setExpanded((v) => !v)} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ textTransform: "uppercase", color: "text.secondary" }} noWrap>
              {produto.descricao}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cód. <LinkProduto codProduto={produto.cod_produto} codEmpresa={produto.cod_empresa} /> · {nomeLoja(produto.cod_empresa)}
            </Typography>
          </Box>
          {solucao && (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: "35%", flexShrink: 1 }} title={solucao}>
              {solucao}
            </Typography>
          )}
          {produto.valor_risco ? (
            <Typography variant="h6" color="error.main" sx={{ whiteSpace: "nowrap" }}>
              {formatCurrency(produto.valor_risco)}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {formatNumero(produto.estoque_disponivel)} un.
          </Typography>
          <ProblemaChip codProblema={produto.cod_problema} nome={produto.problema} />
        </Stack>
      </CardActionArea>
      {/* Fora do CardActionArea, como nos cards da tela inicial. */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <GradeInfo
            mt={0}
            linhas={[
              { label: "Estoque Atual", valor: `${formatNumero(produto.estoque_disponivel)} un.` },
              { label: "Estoque Mínimo", valor: `${formatNumero(produto.qt_min)} un.` },
              { label: "Estoque Máximo", valor: `${formatNumero(produto.qt_max)} un.` },
              { label: "Média Venda Diária", valor: `${formatNumero(produto.mdv)} un./dia` },
              ...(produto.cobertura !== null ? [{ label: "Dias de Cobertura", valor: `${formatNumero(produto.cobertura, 1)} dias` }] : []),
              { label: "Categoria", valor: produto.categoria },
              { label: "Comprador", valor: produto.nome_comprador },
              ...(solucao ? [{ label: "Problema", valor: solucao }, { label: "Responsável", valor: produto.responsavel }] : []),
              { label: "Fornecedor", valor: produto.fornecedor },
              ...(produto.acao_sugerida ? [{ label: "Ação Sugerida", valor: produto.acao_sugerida }] : []),
              ...(produto.valor_risco
                ? [{ label: "Estimativa de Perda", valor: formatCurrency(produto.valor_risco), cor: "error.main", dica: DICA_ESTIMATIVA_PERDA }]
                : []),
            ]}
          />
        </Box>
      </Collapse>

      {expanded && <RodapeCard label="Ver detalhe →" onClick={onVerDetalhe} />}
    </Card>
  );
}

// Produto como um todo: soma as lojas em que ele aparece nas linhas já carregadas.
type ProdutoAgrupado = {
  cod_produto: string;
  descricao: string;
  categoria: string;
  lojas: RupturaProduto[];
  estoque: number;
  qt_min: number;
  qt_max: number;
  mdv: number;
  valor_risco: number;
};

function agruparProdutos(produtos: RupturaProduto[]): ProdutoAgrupado[] {
  const grupos = new Map<string, ProdutoAgrupado>();
  for (const p of produtos) {
    let g = grupos.get(p.cod_produto);
    if (!g) {
      g = { cod_produto: p.cod_produto, descricao: p.descricao, categoria: p.categoria, lojas: [], estoque: 0, qt_min: 0, qt_max: 0, mdv: 0, valor_risco: 0 };
      grupos.set(p.cod_produto, g);
    }
    g.lojas.push(p);
    g.estoque += p.estoque_disponivel;
    g.qt_min += p.qt_min;
    g.qt_max += p.qt_max;
    g.mdv += p.mdv;
    g.valor_risco += p.valor_risco ?? 0;
  }
  return [...grupos.values()];
}

function ProdutoAgrupadoCard({
  grupo,
  nomeLoja,
  onVerDetalhe,
}: {
  grupo: ProdutoAgrupado;
  nomeLoja: NomeLoja;
  onVerDetalhe: (p: RupturaProduto) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const cobertura = grupo.mdv > 0 ? grupo.estoque / grupo.mdv : null;

  return (
    <Card variant="outlined">
      <CardActionArea component="div" onClick={() => setExpanded((v) => !v)} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ textTransform: "uppercase", color: "text.secondary" }} noWrap>
              {grupo.descricao}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cód. <LinkProduto codProduto={grupo.cod_produto} /> · {grupo.lojas.length} {grupo.lojas.length === 1 ? "loja" : "lojas"}
            </Typography>
          </Box>
          {grupo.valor_risco ? (
            <Typography variant="h6" color="error.main" sx={{ whiteSpace: "nowrap" }}>
              {formatCurrency(grupo.valor_risco)}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {formatNumero(grupo.estoque)} un.
          </Typography>
        </Stack>
      </CardActionArea>
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <GradeInfo
            mt={0}
            linhas={[
              { label: "Estoque Atual", valor: `${formatNumero(grupo.estoque)} un.` },
              { label: "Estoque Mínimo", valor: `${formatNumero(grupo.qt_min)} un.` },
              { label: "Estoque Máximo", valor: `${formatNumero(grupo.qt_max)} un.` },
              { label: "Média Venda Diária", valor: `${formatNumero(grupo.mdv)} un./dia` },
              ...(cobertura !== null ? [{ label: "Dias de Cobertura", valor: `${formatNumero(cobertura, 1)} dias` }] : []),
              { label: "Categoria", valor: grupo.categoria },
              ...(grupo.valor_risco
                ? [{ label: "Estimativa de Perda", valor: formatCurrency(grupo.valor_risco), cor: "error.main", dica: DICA_ESTIMATIVA_PERDA }]
                : []),
            ]}
          />
          <Divider sx={{ my: 1.5 }} />
          <Stack gap={1}>
            {grupo.lojas.map((l) => {
              const problema = l.solucao
                ? resolveEmpresaRef(l.solucao, l.cod_empresa_ref ? nomeLoja(l.cod_empresa_ref) : null)
                : "";
              return (
              <Stack key={l.cod_empresa} direction="row" alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {nomeLoja(l.cod_empresa)}
                </Typography>
                {problema && (
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: "35%", flexShrink: 1 }} title={problema}>
                    {problema}
                  </Typography>
                )}
                {l.valor_risco ? (
                  <Typography variant="body2" fontWeight={700} color="error.main" sx={{ whiteSpace: "nowrap" }}>
                    {formatCurrency(l.valor_risco)}
                  </Typography>
                ) : null}
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {formatNumero(l.estoque_disponivel)} un.
                </Typography>
                <ProblemaChip codProblema={l.cod_problema} nome={l.problema} />
                <Button size="small" onClick={() => onVerDetalhe(l)}>
                  Ver detalhe →
                </Button>
              </Stack>
              );
            })}
          </Stack>
        </Box>
      </Collapse>
    </Card>
  );
}

// Filtro da lista de produtos com busca: digitar filtra as opções; vazio = todos.
type OpcaoFiltro = { valor: string; rotulo: string };

function FiltroAutocomplete({
  label,
  valor,
  opcoes,
  onChange,
  loading = false,
  erro = null,
}: {
  label: string;
  valor: string;
  opcoes: OpcaoFiltro[];
  onChange: (valor: string) => void;
  loading?: boolean;
  erro?: string | null;
}) {
  return (
    <Autocomplete
      size="small"
      options={opcoes}
      value={opcoes.find((o) => o.valor === valor) ?? null}
      onChange={(_, o) => onChange(o?.valor ?? "")}
      getOptionLabel={(o) => o.rotulo}
      isOptionEqualToValue={(o, v) => o.valor === v.valor}
      renderOption={({ key: _key, ...props }, o) => (
        <li key={o.valor} {...props}>
          {o.rotulo}
        </li>
      )}
      loading={loading}
      loadingText="Carregando..."
      noOptionsText={erro ?? "Nenhuma opção"}
      renderInput={(params) => <TextField {...params} placeholder={label} />}
      sx={{ minWidth: 220, maxWidth: 320, flex: "1 1 220px" }}
    />
  );
}

// Filtro extra da lista de produtos: as opções são as linhas do resumo da dimensão.
function FiltroOpcoes({
  dimensao,
  valor,
  onChange,
  clientId,
  nomeLoja,
  filtro = SEM_FILTRO,
}: {
  dimensao: RupturaDimensao;
  valor: string;
  onChange: (valor: string) => void;
  clientId: string;
  nomeLoja: NomeLoja;
  // Com responsável escolhido, o filtro de problema só oferece os problemas dele.
  filtro?: RupturaFiltros;
}) {
  const { dados, erro, loading } = useRupturaResumo(dimensao, filtro, clientId);

  const opcoes = useMemo(
    () =>
      (dados ?? [])
        .map((o) => ({ valor: o.chave, rotulo: dimensao === "loja" ? nomeLoja(o.chave) : o.rotulo }))
        // "Sem X" é opção como as outras (a lista pode chegar filtrada por ela), só que no fim.
        .sort(
          (a, b) =>
            Number(ehSemValor(dimensao, a.valor)) - Number(ehSemValor(dimensao, b.valor)) ||
            a.rotulo.localeCompare(b.rotulo, "pt-BR", { numeric: true, sensitivity: "base" }),
        ),
    [dados, dimensao, nomeLoja],
  );

  return (
    <FiltroAutocomplete
      label={DIMENSAO_LABELS[dimensao]}
      valor={valor}
      opcoes={opcoes}
      onChange={onChange}
      loading={loading}
      erro={erro}
    />
  );
}

// Totalizadores acima da lista: itens (produto x loja) e quantos em cada situação, pelos
// filtros e busca da lista menos a situação. Clicar numa situação filtra a lista por ela.
function TotaisLista({
  filtros,
  busca,
  situacao,
  onSituacao,
  clientId,
}: {
  filtros: RupturaFiltros;
  busca: string;
  situacao: string;
  onSituacao: (situacao: string) => void;
  clientId: string;
}) {
  const lojas = useContext(LojasRupturaContext);
  const { cod_problema: _, ...semSituacao } = filtros;
  const { dados } = useRupturaBusca(
    lojas.length > 0 ? `produtos_total:${clientId}:${lojas.join(",")}:${JSON.stringify(semSituacao)}:${busca.trim()}` : null,
    () => getRupturaProdutosTotal({ ...semSituacao, busca, lojas }, clientId),
    "Não foi possível carregar os totais.",
  );
  if (!dados) return null;

  const blocos = [
    { situacao: "", label: "Total", qtd: dados.qtd_total, detalhe: `${formatNumero(dados.qtd_produtos, 0)} ${dados.qtd_produtos === 1 ? "produto" : "produtos"}` },
    { situacao: "1", label: PROBLEMAS[1]!, qtd: dados.qtd_ruptura, cor: dados.qtd_ruptura > 0 ? "error.main" : undefined },
    { situacao: "3", label: PROBLEMAS[3]!, qtd: dados.qtd_excesso },
    { situacao: "5", label: PROBLEMAS[5]!, qtd: dados.qtd_ok },
  ];

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
      {blocos.map((b) => {
        const ativo = b.situacao === situacao;
        return (
          <Card
            key={b.label}
            variant="outlined"
            sx={{ borderColor: ativo ? "primary.main" : undefined, borderWidth: ativo ? 2 : undefined }}
          >
            <CardActionArea onClick={() => onSituacao(b.situacao)} sx={{ px: 1.5, py: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {b.label}
              </Typography>
              <Typography variant="h6" fontWeight={700} color={b.cor} sx={{ lineHeight: 1.2 }}>
                {formatNumero(b.qtd, 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {b.detalhe ?? formatPercentage(percDe(b.qtd, dados.qtd_total))}
              </Typography>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
}

// O que a lista tem aplicado agora; a situação vai em filtros.cod_problema.
export type EstadoLista = { filtros: RupturaFiltros; busca: string; agrupar: boolean };

export function ProdutosView({
  filtrosIniciais,
  buscaInicial = "",
  agruparInicial = false,
  clientId,
  nomeLoja,
  onVoltar,
  onVerDetalhe,
  onMudar,
}: {
  // Vindos do atalho que abriu a lista (linha e card de origem + situação da linha da grade)
  // ou da URL; todos aparecem nos filtros e o usuário pode trocar qualquer um.
  filtrosIniciais: RupturaFiltros;
  buscaInicial?: string;
  agruparInicial?: boolean;
  clientId: string;
  nomeLoja: NomeLoja;
  onVoltar: () => void;
  onVerDetalhe: (p: RupturaProduto) => void;
  onMudar?: (estado: EstadoLista) => void;
}) {
  const [produtos, setProdutos] = useState<RupturaProduto[]>([]);
  const [pagina, setPagina] = useState(0);
  const [temMais, setTemMais] = useState(false);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [agrupar, setAgrupar] = useState(agruparInicial);
  const agrupados = useMemo(() => (agrupar ? agruparProdutos(produtos) : []), [agrupar, produtos]);
  // busca = o que está digitado; buscaAplicada = o que vai na query (após o debounce).
  const [busca, setBusca] = useState(buscaInicial);
  const [buscaAplicada, setBuscaAplicada] = useState(buscaInicial);
  // "" = todas as situações.
  const [situacao, setSituacao] = useState(filtrosIniciais.cod_problema ? String(filtrosIniciais.cod_problema) : "");
  // Filtros das dimensões; a situação fica à parte.
  const [filtros, setFiltros] = useState<RupturaFiltros>(() => ({ ...filtrosIniciais, cod_problema: null }));
  const temFiltro = !!situacao || DIMENSOES.some((d) => chaveDoFiltro(filtros, d) !== null);

  const limparFiltros = () => {
    setSituacao("");
    setFiltros({});
    setPagina(0);
  };

  const aplicarBusca = useDebounce(
    useCallback((texto: string) => {
      setBuscaAplicada(texto);
      setPagina(0);
    }, []),
    500,
  );

  const lojas = useContext(LojasRupturaContext);

  // Avisa o pai a cada mudança (não na montagem, que já parte do estado dele).
  const onMudarRef = useRef(onMudar);
  onMudarRef.current = onMudar;
  const montou = useRef(false);
  useEffect(() => {
    if (!montou.current) {
      montou.current = true;
      return;
    }
    onMudarRef.current?.({
      filtros: { ...filtros, cod_problema: situacao ? Number(situacao) : null },
      busca: buscaAplicada,
      agrupar,
    });
  }, [filtros, situacao, buscaAplicada, agrupar]);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setErro(null);
    getRupturaProdutos(
      { pagina, busca: buscaAplicada, lojas, ...filtros, cod_problema: situacao ? Number(situacao) : null },
      clientId,
    )
      .then((novos) => {
        if (cancelado) return;
        setProdutos((atuais) => (pagina === 0 ? novos : [...atuais, ...novos]));
        setTemMais(novos.length === PRODUTOS_POR_PAGINA);
      })
      .catch((e) => !cancelado && setErro(e?.message ?? "Não foi possível carregar os produtos."))
      .finally(() => !cancelado && setLoading(false));
    return () => {
      cancelado = true;
    };
  }, [pagina, clientId, buscaAplicada, situacao, filtros, lojas]);

  return (
    <Stack gap={2}>
      <Voltar label="Voltar" onClick={onVoltar} />
      <Typography variant="subtitle1" fontWeight={700}>
        Produtos
      </Typography>

      <TextField
        size="small"
        placeholder="Nome, código ou código de barras do produto..."
        value={busca}
        onChange={(e) => {
          setBusca(e.target.value);
          aplicarBusca(e.target.value);
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        <FiltroAutocomplete
          label="Situação"
          valor={situacao}
          opcoes={SITUACOES.map((cod) => ({ valor: String(cod), rotulo: PROBLEMAS[cod]! }))}
          onChange={(v) => {
            setSituacao(v);
            setPagina(0);
          }}
        />
        {DIMENSOES.map((d) => (
          <FiltroOpcoes
            key={d}
            dimensao={d}
            valor={chaveDoFiltro(filtros, d) ?? ""}
            onChange={(v) => {
              // Trocar o responsável limpa o problema, que pode não ser daquele responsável.
              setFiltros((atuais) => ({
                ...atuais,
                ...(d === "responsavel" ? { cod_solucao: null } : {}),
                ...filtroDaDimensao(d, v),
              }));
              setPagina(0);
            }}
            clientId={clientId}
            nomeLoja={nomeLoja}
            filtro={d === "subproblema" ? { cod_responsavel: filtros.cod_responsavel ?? null } : SEM_FILTRO}
          />
        ))}
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <FormControlLabel
          control={<Checkbox size="small" checked={agrupar} onChange={(e) => setAgrupar(e.target.checked)} />}
          label="Agrupar produtos"
          sx={{ mr: 0 }}
        />
        <Button size="small" onClick={limparFiltros} disabled={!temFiltro}>
          Limpar filtros
        </Button>
      </Stack>

      <TotaisLista
        filtros={filtros}
        busca={buscaAplicada}
        situacao={situacao}
        onSituacao={(v) => {
          setSituacao(v);
          setPagina(0);
        }}
        clientId={clientId}
      />

      {erro && <Alert severity="error">{erro}</Alert>}
      {loading && pagina === 0 ? (
        <LinearProgress />
      ) : produtos.length === 0 && !erro ? (
        <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
          Não há produtos para apresentar.
        </Typography>
      ) : (
        <Stack gap={1}>
          {agrupar
            ? agrupados.map((g) => (
                <ProdutoAgrupadoCard key={g.cod_produto} grupo={g} nomeLoja={nomeLoja} onVerDetalhe={onVerDetalhe} />
              ))
            : produtos.map((p) => (
                <ProdutoCard key={`${p.cod_empresa}-${p.cod_produto}`} produto={p} nomeLoja={nomeLoja} onVerDetalhe={() => onVerDetalhe(p)} />
              ))}
        </Stack>
      )}
      {temMais && (
        <Box sx={{ textAlign: "center" }}>
          <Button onClick={() => setPagina((p) => p + 1)} disabled={loading}>
            {loading ? "Carregando..." : "Carregar mais"}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
